// The builder's schedule after an Extend flight (campaign lifecycle 2b, 2026-10-21).
//
// The builder seeds its budget, dates and flights from the plan once, on mount, and never
// reads the plan again: a published plan was frozen, so nothing needed to. An extension is
// the one change a published plan takes, and the page must show it without a remount, which
// would drop every open section and the side column's state. So the terms are replaced in
// place from the plan that carries the new flight: at once from the Meta route's answer
// (`apply`), and, for a queued The Trade Desk extension, when the plan the page reads gains
// a flight the builder does not have (the commit's refetch).

import { useCallback, useEffect, type Dispatch, type SetStateAction } from 'react';
import type { CampaignPlan } from '@/api/types';
import type { CampaignFlight } from '@/lib/campaign-flights';
import { planAddsFlights, planTerms } from '@/lib/flight-extension';

export interface PlanTermsRefreshInput {
  /** The plan the route reads, refetched after a commit. */
  plan: CampaignPlan | null | undefined;
  /** Only a published plan takes an extension; a draft's terms are the operator's. */
  wasLive: boolean;
  flights: readonly CampaignFlight[];
  setBudget: Dispatch<SetStateAction<number>>;
  setStartDate: Dispatch<SetStateAction<string>>;
  setEndDate: Dispatch<SetStateAction<string>>;
  setFlights: Dispatch<SetStateAction<CampaignFlight[]>>;
  setEditedFlights: Dispatch<SetStateAction<Set<string>>>;
  setSavedPlan: Dispatch<SetStateAction<CampaignPlan | null>>;
  /** Cleared so the saved fingerprint is taken again over the new terms. */
  setSavedFingerprint: Dispatch<SetStateAction<string | null>>;
}

/** Returns `apply`, which replaces the schedule terms from a plan. */
export function usePlanTermsRefresh({
  plan,
  wasLive,
  flights,
  setBudget,
  setStartDate,
  setEndDate,
  setFlights,
  setEditedFlights,
  setSavedPlan,
  setSavedFingerprint,
}: PlanTermsRefreshInput): (next: CampaignPlan) => void {
  const apply = useCallback(
    (next: CampaignPlan) => {
      const terms = planTerms(next);
      setBudget(terms.budget);
      setStartDate(terms.startDate);
      setEndDate(terms.endDate);
      setFlights(terms.flights);
      setEditedFlights(new Set());
      setSavedPlan(next);
      setSavedFingerprint(null);
    },
    [
      setBudget,
      setStartDate,
      setEndDate,
      setFlights,
      setEditedFlights,
      setSavedPlan,
      setSavedFingerprint,
    ],
  );
  useEffect(() => {
    // Stops itself: once applied, the builder has every flight the plan has.
    if (plan && wasLive && planAddsFlights(plan, flights)) apply(plan);
  }, [plan, wasLive, flights, apply]);
  return apply;
}
